import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { flushQueue, getQueueCount, subscribeQueue, getPendingIds } from "@/lib/offline";
import { toast } from "sonner";

const LAST_SYNC_KEY = "prosperr_last_sync";

const OfflineCtx = createContext({
  isOnline: true,
  pending: 0,
  syncing: false,
  lastSync: null,
  pendingIds: new Set(),
  triggerSync: () => {},
});

export function OfflineProvider({ children }) {
  const [isOnline, setIsOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [pending, setPending] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [pendingIds, setPendingIds] = useState(() => new Set());
  const [lastSync, setLastSync] = useState(() => {
    try {
      const v = localStorage.getItem(LAST_SYNC_KEY);
      return v ? parseInt(v, 10) : null;
    } catch { return null; }
  });

  const refreshCount = useCallback(async () => {
    const c = await getQueueCount();
    setPending(c);
    const ids = await getPendingIds();
    setPendingIds(ids);
  }, []);

  const triggerSync = useCallback(async () => {
    if (!navigator.onLine || syncing) return;
    setSyncing(true);
    try {
      const { flushed, failed } = await flushQueue(api);
      if (flushed > 0) toast.success(`${flushed} modification${flushed > 1 ? "s" : ""} synchronisée${flushed > 1 ? "s" : ""}`);
      if (failed > 0) toast.error(`${failed} modification${failed > 1 ? "s" : ""} rejetée${failed > 1 ? "s" : ""} par le serveur`);
      const now = Date.now();
      try { localStorage.setItem(LAST_SYNC_KEY, String(now)); } catch (_) { /* swallow: non-fatal */ }
      setLastSync(now);
    } finally {
      setSyncing(false);
      await refreshCount();
    }
  }, [syncing, refreshCount]);

  useEffect(() => {
    const onOnline = () => { setIsOnline(true); triggerSync(); };
    const onOffline = () => setIsOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [triggerSync]);

  useEffect(() => {
    refreshCount();
    const unsub = subscribeQueue(refreshCount);
    return () => unsub();
  }, [refreshCount]);

  // Try sync on mount if already online with pending items
  useEffect(() => {
    if (isOnline) triggerSync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <OfflineCtx.Provider value={{ isOnline, pending, syncing, lastSync, pendingIds, triggerSync }}>
      {children}
    </OfflineCtx.Provider>
  );
}

export function useOffline() {
  return useContext(OfflineCtx);
}
